import type { FC } from 'react';
import { useState, useEffect, useCallback, useRef } from 'react';
import { createUseStyles } from 'react-jss';
import { Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';

import { useComparisonProviderContext } from '../../../providers/ComparisonProvider';
import { useMapData } from '../../../map-data/use-map-data';

import { PoiPicker } from './PoiPicker';
import {
  getPoiComparisonLocalStorage,
  setPoiComparisonLocalStorage,
} from './use-comparison-local-storage';

const useStyles = createUseStyles((theme: any) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: `calc(100vh - ${theme.spacing(30)})`,
    background: '#F0F0F0',
    overflowY: 'scroll',
  },
  helpTextContainer: {
    background: '#3D434F',
    minHeight: theme.spacing(15),
    maxHeight: theme.spacing(15),
    display: 'flex',
    alignItems: 'center',
    color: 'white',
    padding: theme.spacing(0, 5),
    fontSize: theme.fontSizes.p1,
    justifyContent: 'space-between',
    alignContent: 'center',
  },
  ctaWrapper: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
  },
  pickerContainer: {
    background: 'white',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  picker: {
    background: theme?.colors?.['blue-100'] || '#155181',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    padding: theme.spacing(12, 0),
    width: '100%',
    height: '100%',
    border: 'none',
  },
  pickerActionText: {
    paddingTop: theme.spacing(4),
    color: 'white',
    fontSize: theme.fontSizes.h4,
  },
}));

const MAX_COMPARISONS = 4;
const MIN_COMPARISONS = 2;

interface PickerFormState {
  country: string;
  city: string;
  studyId: string;
  key: string; // Unique key to force re-mounting
}

export const PoiStudyComparisonPicker: FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { poiComparison, setPoiComparison } = useComparisonProviderContext();
  const { studiesById } = useMapData();

  // Track the last synced version from context to avoid re-syncing unnecessarily
  const lastSyncedVersion = useRef<number | undefined>(undefined);

  // Initialize state from query string context first, then fall back to localStorage
  const [pickerStates, setPickerStates] = useState<PickerFormState[]>(() => {
    // Check if we have data from query string in context
    if (poiComparison.studyIds && poiComparison.studyIds.length > 0) {
      lastSyncedVersion.current = poiComparison.syncVersion;
      return poiComparison.studyIds.map((studyId, index) => {
        // Look up study data to get country and city
        const study = studiesById?.[studyId];
        return {
          country: study?.country || '',
          city: study?.city || '',
          studyId,
          key: `picker-${Date.now()}-${index}`,
        };
      });
    }

    // Fall back to localStorage
    const savedStates = getPoiComparisonLocalStorage();
    // Filter out empty saved states (ones without a study)
    const validSavedStates = savedStates.filter(saved => saved.study);

    if (validSavedStates.length > 0) {
      return validSavedStates.map((saved, index) => ({
        country: saved.country || '',
        city: saved.city || '',
        studyId: saved.study || '',
        key: `picker-${Date.now()}-${index}`,
      }));
    }
    return [
      { country: '', city: '', studyId: '', key: `picker-${Date.now()}-0` },
    ];
  });

  // Track confirmed state for each picker separately
  // If we have data from context or savedStates, assume they were confirmed
  const [confirmedStates, setConfirmedStates] = useState<boolean[]>(() => {
    // Check if we have data from query string in context
    if (poiComparison.studyIds && poiComparison.studyIds.length > 0) {
      return poiComparison.studyIds.map(() => true); // All context states are confirmed
    }

    // Fall back to localStorage
    const savedStates = getPoiComparisonLocalStorage();
    const validSavedStates = savedStates.filter(saved => saved.study);

    if (validSavedStates.length > 0) {
      return validSavedStates.map(() => true); // All valid saved states are confirmed
    }
    return [false];
  });

  // Get all selected study IDs from picker states (including non-confirmed forms)
  const allSelectedStudyIds = pickerStates
    .map(state => state.studyId)
    .filter(Boolean);

  // Check for duplicates
  const hasDuplicates =
    allSelectedStudyIds.length > 0 &&
    new Set(allSelectedStudyIds).size !== allSelectedStudyIds.length;

  // Disable generate button if less than 2 selections or any duplicates
  // Note: confirmed state no longer required - Generate will auto-confirm
  const disableGenerateButton =
    allSelectedStudyIds.length < MIN_COMPARISONS || hasDuplicates;

  const handleFormChange = useCallback(
    (index: number, formState: Omit<PickerFormState, 'key'>) => {
      setPickerStates(prev => {
        const updated = [...prev];
        // Preserve the existing key
        updated[index] = { ...formState, key: prev[index].key };
        return updated;
      });
    },
    [],
  );

  const handleConfirm = useCallback((index: number, confirmed: boolean) => {
    setConfirmedStates(prev => {
      const updated = [...prev];
      updated[index] = confirmed;
      return updated;
    });
  }, []);

  const handleAddPicker = () => {
    if (pickerStates.length < MAX_COMPARISONS) {
      setPickerStates(prev => [
        ...prev,
        {
          country: '',
          city: '',
          studyId: '',
          key: `picker-${Date.now()}-${prev.length}`,
        },
      ]);
      setConfirmedStates(prev => [...prev, false]);
    }
  };

  const handleRemovePicker = (index: number) => {
    setPickerStates(prev => prev.filter((_, i) => i !== index));
    setConfirmedStates(prev => prev.filter((_, i) => i !== index));
  };

  const handleGenerate = () => {
    // Auto-confirm all pickers with valid selections
    setConfirmedStates(pickerStates.map(state => !!state.studyId));

    // Generate with all selected study IDs (not just previously confirmed)
    setPoiComparison({
      display: true,
      studyIds: allSelectedStudyIds,
    });
  };

  // Save picker states to localStorage whenever they change
  // Only save states that have at least a study selected
  useEffect(() => {
    const statesToSave = pickerStates
      .filter(state => state.studyId) // Only save states with a study
      .map(state => ({
        country: state.country,
        city: state.city,
        study: state.studyId,
      }));
    setPoiComparisonLocalStorage(statesToSave);
  }, [pickerStates]);

  // Initialize with 1 empty picker if none exist
  useEffect(() => {
    if (pickerStates.length === 0) {
      setPickerStates([
        { country: '', city: '', studyId: '', key: `picker-${Date.now()}-0` },
      ]);
      setConfirmedStates([false]);
    }
  }, [pickerStates.length]);

  // Reinitialize picker when context syncVersion changes
  // This happens when context is updated from external sources (query string, favourites, etc.)
  useEffect(() => {
    if (
      poiComparison.studyIds &&
      poiComparison.studyIds.length > 0 &&
      studiesById &&
      poiComparison.syncVersion !== lastSyncedVersion.current
    ) {
      const contextStudyIds = poiComparison.studyIds;

      // Reinitialize from context
      const newPickerStates = contextStudyIds.map((studyId, index) => {
        const study = studiesById[studyId];
        return {
          country: study?.country || '',
          city: study?.city || '',
          studyId,
          key: `picker-${Date.now()}-${index}`,
        };
      });
      setPickerStates(newPickerStates);
      setConfirmedStates(contextStudyIds.map(() => true));

      // Remember that we've synced this version
      lastSyncedVersion.current = poiComparison.syncVersion;
    }
  }, [poiComparison.syncVersion, poiComparison.studyIds, studiesById]);

  return (
    <div className={classes.container}>
      <div className={classes.helpTextContainer}>
        <p>{t('comparisonPicker.poi.helpText')}</p>
        {!disableGenerateButton && (
          <Button onClick={handleGenerate}>
            {t('comparisonPicker.poi.generateCta')}
          </Button>
        )}
      </div>
      <Box sx={{ margin: 4 }}>
        <Grid container spacing={2} alignItems="stretch">
          {pickerStates.map((pickerState, index) => {
            // Exclude study IDs from other pickers
            const excludedStudyIds = pickerStates
              .filter((_, i) => i !== index)
              .map(state => state.studyId)
              .filter(Boolean);

            return (
              <Grid item xs={12} md={6} lg={3} key={pickerState.key}>
                <PoiPicker
                  selectionNumber={(index + 1) as 1 | 2}
                  initialCountry={pickerState.country}
                  initialCity={pickerState.city}
                  initialStudyId={pickerState.studyId}
                  initialIsConfirmed={confirmedStates[index]}
                  excludedStudyIds={excludedStudyIds}
                  onFormChange={formState => handleFormChange(index, formState)}
                  onConfirm={confirmed => handleConfirm(index, confirmed)}
                  onRemove={
                    pickerStates.length > 1
                      ? () => handleRemovePicker(index)
                      : undefined
                  }
                />
              </Grid>
            );
          })}
          {pickerStates.length < MAX_COMPARISONS && (
            <Grid item xs={12} md={6} lg={3}>
              <div className={classes.pickerContainer}>
                <button className={classes.picker} onClick={handleAddPicker}>
                  <div
                    style={{
                      fontSize: '3rem',
                      fontWeight: 300,
                      color: 'white',
                      lineHeight: 1,
                    }}
                  >
                    +
                  </div>
                  <p className={classes.pickerActionText}>Add another study</p>
                </button>
              </div>
            </Grid>
          )}
        </Grid>
      </Box>
      <Box
        sx={{
          justifyContent: 'center',
          display: 'flex',
          paddingBottom: 8,
        }}
      >
        <Button
          disabled={disableGenerateButton}
          onClick={handleGenerate}
          sx={{
            mt: 2,
          }}
        >
          {t('comparisonPicker.poi.generateCta')}
        </Button>
      </Box>
    </div>
  );
};
